// src/components/requests/OfferCard.jsx
import React, { useState } from 'react';

const OfferCard = ({ offer, index, onAccept, onContact, showRank = false, primaryColor = '#6366f1' }) => {
  const [contactOpen, setContactOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const fitReasons = Array.isArray(offer.fitReasons) ? offer.fitReasons : [];
  const riskFlags = Array.isArray(offer.riskFlags) ? offer.riskFlags : [];

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (onContact) onContact(offer, message);

    setSending(false);
    setContactOpen(false);
    setMessage('');
  };

  return (
    <>
      <div
        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 shadow-sm flex flex-col animate-fade-in"
        style={{ animationDelay: `${index * 80}ms` }}
      >
        {showRank && (
          <div className="mb-4 flex items-center justify-between">
            <span
              className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide text-white"
              style={{ backgroundColor: primaryColor }}
            >
              Rank #{index + 1}
            </span>
            {typeof offer.fitScore === 'number' && (
              <span className="text-xs font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                Fit {offer.fitScore}
              </span>
            )}
          </div>
        )}

        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">
              {offer.providerName?.charAt(0) || 'P'}
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">{offer.providerName || 'Provider'}</h3>
              <p className="text-xs text-slate-500">{offer.providerType || 'Service Provider'}</p>
            </div>
          </div>

          <div className="flex items-center text-yellow-600">
            <span aria-hidden="true">★</span>
            <span className="ml-1 text-sm">{offer.rating || 'N/A'}</span>
          </div>
        </div>

        <h4 className="font-bold text-lg text-slate-800 dark:text-slate-200 mb-2">
          {offer.offerTitle || offer.presetName || offer.providerName || 'Qualified Offer'}
        </h4>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 line-clamp-3">
          {offer.description || offer.valueProp || 'Qualified offer prepared for this mission.'}
        </p>

        {fitReasons.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {fitReasons.slice(0, 3).map((reason) => (
              <span key={reason} className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
                {reason}
              </span>
            ))}
          </div>
        )}

        {riskFlags.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-300">
            {riskFlags.join(' • ')}
          </div>
        )}

        <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-auto">
          <div className="flex justify-between mb-4 gap-4">
            <div>
              <p className="text-xs text-slate-400 uppercase">Estimate</p>
              <p className="text-xl font-semibold text-indigo-600">
                {offer.normalizedPriceLabel || offer.priceEstimate || offer.price || 'Custom quote'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400 uppercase">Availability</p>
              <p className="text-sm font-medium">
                {offer.normalizedTimeframe || offer.availability || offer.timeframe || 'Flexible'}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              className="flex-1 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
              onClick={() => setContactOpen(true)}
            >
              Contact
            </button>

            <button
              className="flex-1 py-2 text-white rounded-lg text-sm hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
              onClick={() => onAccept && onAccept(offer)}
            >
              Approve
            </button>
          </div>
        </div>
      </div>

      {contactOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md p-6 border border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-lg mb-4">
              Contact {offer.providerName}
            </h3>

            <form onSubmit={handleSend}>
              <textarea
                rows={5}
                className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 mb-4"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setContactOpen(false)}
                  className="px-4 py-2 text-sm bg-slate-200 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg"
                >
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default OfferCard;
